import React, { useState, useContext } from "react";
import { Box, Typography } from "@mui/material";
import { getColors } from "../../themes/theme";
import { ThemeContext } from "../../context/ThemeContext";
import BorderColorOutlinedIcon from "@mui/icons-material/BorderColorOutlined";
import { Add } from "@mui/icons-material";
import { EditTreatmentDialog } from "../treatment/EditTreatmentDialog";
import { useAuthContext } from "../../hooks/contexts/useAuthContext";
import axios from "axios";
import { useSnackbar } from "notistack";
import { CopyTreatmentDialog } from "./CopyTreatmentDialog";

export const TreatmentItem = ({ id, name, onTreatmentUpdate }) => {
  const { token } = useAuthContext();
  const { darkMode } = useContext(ThemeContext);
  const [openEditDialog, setOpenEditDialog] = useState(false); // New state for confirmation dialog
  const [openCopyDialog, setOpenCopyDialog] = useState(false); // New state for confirmation dialog
  const [treatmentToEdit, setTreatmentToEdit] = useState(null); // New state for the user to be deleted
  const { enqueueSnackbar } = useSnackbar();

  const handleClick = async () => {
    const response = await axios.get(
      `${process.env.REACT_APP_API_URL}/treatment/${id}`,
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );
    setTreatmentToEdit(response.data);
    setOpenEditDialog(true);
  };

  const handleCopyClick = async () => {
    const response = await axios.get(
      `${process.env.REACT_APP_API_URL}/treatment/${id}`,
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );
    setTreatmentToEdit(response.data);
    setOpenCopyDialog(true);
  };

  const handleCancelEdit = () => {
    setOpenEditDialog(false);
  };

  const handleCancelCopy = () => {
    setOpenCopyDialog(false);
  };

  const handleEditTreatment = async (data) => {
    try {
      const response = await axios.put(
        `${process.env.REACT_APP_API_URL}/treatment/${data.id}`,
        {
          ...data,
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      onTreatmentUpdate(response.data); // Update the parent state
      enqueueSnackbar("Successfully Changed!", { variant: "success" });
    } catch (error) {
      enqueueSnackbar("Failed to update treatment!", { variant: "error" });
    }
  };

  const handleCopyTreatment = async (data) => {
    try {
      const response = await axios.post(
        `${process.env.REACT_APP_API_URL}/treatment`,
        {
          ...data,
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      onTreatmentUpdate(response.data); // Update the parent state
      enqueueSnackbar("Successfully Copied!", { variant: "success" });
    } catch (error) {
      enqueueSnackbar("Failed to copy treatment!", { variant: "error" });
    }
  };

  const handleDeleteTreatment = async (id) => {
    try {
      await axios.delete(`${process.env.REACT_APP_API_URL}/treatment/${id}`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      onTreatmentUpdate({ id, deleted: true }); // Notify parent about the deletion
      setOpenEditDialog(false);
      enqueueSnackbar("Successfully Deleted!", { variant: "success" });
    } catch (error) {
      enqueueSnackbar("Failed to delete treatment!", { variant: "error" });
    }
  };

  return (
    <>
      <Box
        display={"flex"}
        justifyContent={"space-between"}
        alignItems={"center"}
        sx={{
          width: "475px",
          height: "120px",
          padding: 1,
          borderRadius: "5px",
          borderWidth: 1,
          borderStyle: "solid",
          borderColor: darkMode
            ? getColors.thumbnailBorderDark
            : getColors.thumbnailBorderLight,
          background: darkMode
            ? getColors.thumbnnailBgDark
            : getColors.thumbnnailBgLight,
          cursor: "pointer",
          "&:hover": {
            borderColor: darkMode
              ? getColors.thumbnailTextDark
              : getColors.thumbnailTextLight,
          },
        }}
      >
        <Box display={"flex"} alignItems={"center"}>
          <Typography
            sx={{
              fontSize: "22px",
              fontWeight: 500,
              color: darkMode
                ? getColors.drawerTextDark
                : getColors.thumbnailTextLight,
              ml: "20px",
            }}
          >
            {name}
          </Typography>
        </Box>
        <Box display="flex">
          <Box
            display={"flex"}
            justifyContent={"flex-end"}
            width="max-content"
            sx={{
              borderRadius: "50%",
              padding: "20px",
              color: "#009903",
              bgcolor: "#EAFFEF",
              alignItems: "center",
              "&:hover": {
                color: "green",
              },
              ml: 1,
              mr: 2,
            }}
            onClick={handleCopyClick} // Move the onClick handler here
          >
            <Add />
          </Box>
          <Box
            display={"flex"}
            justifyContent={"flex-end"}
            width="max-content"
            sx={{
              borderRadius: "50%",
              padding: "20px",
              color: "#007FFF",
              bgcolor: "#E9F3FF",
              alignItems: "center",
              "&:hover": {
                color: "blue",
              },
              mr: 2,
            }}
            onClick={handleClick} // Move the onClick handler here
          >
            <BorderColorOutlinedIcon />
          </Box>
        </Box>
      </Box>
      <EditTreatmentDialog
        open={openEditDialog}
        onClose={handleCancelEdit}
        treatmentData={treatmentToEdit}
        onSave={handleEditTreatment}
        onRemove={handleDeleteTreatment}
      />
      <CopyTreatmentDialog
        open={openCopyDialog}
        onClose={handleCancelCopy}
        treatmentData={treatmentToEdit}
        onSave={handleCopyTreatment}
      />
    </>
  );
};
